import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Sparkles, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * ScratchCardAnnouncer — mounts once inside StoreLayout.
 *
 * Once a customer's order is delivered, the backend mints a
 * scratch card and stamps `announced_at=null`. Next time the app
 * mounts (or the tab regains focus), this component polls
 * `/loyalty/scratch-cards/pending-announcement` and if any cards
 * are waiting, it shows a single "Congrats!" modal.
 *
 * Interaction:
 *   • "Scratch Now"  → navigate to /account/rewards + POST /acknowledge-all
 *   • "Later"        → POST /acknowledge-all (so the modal doesn't re-fire)
 *   • Backdrop / X   → same as "Later"
 *
 * Both CTAs call the server so the popup can never re-appear for
 * the same card on the next visit. If the customer clears their
 * localStorage the modal still stays quiet (source of truth is the
 * `announced_at` field on the DB row).
 */
export default function ScratchCardAnnouncer() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const checkedThisSession = useRef(false);
  const navigate = useNavigate();

  const runCheck = async () => {
    // Cheap guard so we don't spam the endpoint on every re-render.
    if (checkedThisSession.current) return;
    checkedThisSession.current = true;
    try {
      const { data } = await api.get("/loyalty/scratch-cards/pending-announcement");
      if ((data.count || 0) > 0) {
        setCount(data.count);
        setOpen(true);
      }
    } catch {
      // Silent — a loyalty hiccup shouldn't intrude on browsing.
    }
  };

  useEffect(() => {
    if (!user) {
      // Reset so the check runs again the next time a customer
      // logs in on this device.
      checkedThisSession.current = false;
      return;
    }
    runCheck();
    // Re-check when the tab regains focus so a customer who kept
    // the app open in a background tab still sees the popup after
    // their order is delivered on the seller side.
    const onFocus = () => {
      // Reset the once-per-session flag; the API is idempotent so
      // the second call is cheap when there's nothing new.
      checkedThisSession.current = false;
      runCheck();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user?.id]);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post("/loyalty/scratch-cards/acknowledge-all");
    } catch {
      // Even if the ack fails, close the modal locally so we
      // don't trap the user. Next server check will retry silently.
    }
    setBusy(false);
    setOpen(false);
  };

  const scratchNow = async () => {
    await acknowledge();
    navigate("/account/rewards");
  };

  if (!open) return null;

  const one = count === 1;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={acknowledge}
      data-testid="scratch-announcer-backdrop"
    >
      <div
        className="relative w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="scratch-announcer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scratch-announcer-title"
      >
        <button
          type="button"
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/5 z-10"
          onClick={acknowledge}
          disabled={busy}
          aria-label="Close"
          data-testid="scratch-announcer-close"
        >
          <X size={18} className="text-neutral-600" />
        </button>

        {/* Confetti-ish header */}
        <div className="relative h-32 bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, #fff 0 2px, transparent 2px), " +
                "radial-gradient(circle at 80% 20%, #fff 0 1.5px, transparent 1.5px), " +
                "radial-gradient(circle at 50% 70%, #fff 0 1.5px, transparent 1.5px)",
              backgroundSize: "60px 60px, 40px 40px, 80px 80px",
            }}
          />
          <div className="relative flex flex-col items-center text-white">
            <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
              <Gift size={32} strokeWidth={2} />
            </div>
            <div className="flex items-center gap-1 mt-2 text-xs uppercase tracking-widest">
              <Sparkles size={12} /> Congrats
            </div>
          </div>
        </div>

        <div className="p-5 text-center">
          <h2
            id="scratch-announcer-title"
            className="font-display text-xl mb-2"
            data-testid="scratch-announcer-title"
          >
            {one
              ? "You've earned a scratch card!"
              : `You have ${count} scratch cards waiting!`}
          </h2>
          <p className="text-sm text-neutral-600 mb-5" data-testid="scratch-announcer-body">
            {one
              ? "Your recent delivery unlocked a reward. Tap the card and scratch it to claim your coins."
              : `Your recent deliveries unlocked ${count} rewards. Tap each card and scratch to claim.`}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={acknowledge}
              disabled={busy}
              className="py-2.5 rounded-full text-sm border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"
              data-testid="scratch-announcer-later"
            >
              Later
            </button>
            <button
              type="button"
              onClick={scratchNow}
              disabled={busy}
              className="py-2.5 rounded-full text-sm text-white bg-gradient-to-r from-amber-500 to-rose-500 hover:opacity-90 disabled:opacity-40 shadow"
              data-testid="scratch-announcer-cta"
            >
              Scratch Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
